import cn from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import updateConditionActiveOptionOperation from 'editor/src/store/design/operation/updateConditionActiveOptionOperation';
import updateMediaElementOperation, {
  MediaUpdateActionName,
} from 'editor/src/store/design/operation/updateMediaElementOperation';
import updateSharedTextFieldOperation from 'editor/src/store/design/operation/updateSharedTextFieldOperation';
import updateTextConditionVariantsOperation from 'editor/src/store/design/operation/updateTextConditionVariantsOperation';
import getConditionById from 'editor/src/store/design/selector/getConditionById';
import getDesignConditions from 'editor/src/store/design/selector/getDesignConditions';
import getMediaElementByUuid from 'editor/src/store/design/selector/getMediaElementByUuid';
import getSharedTextFieldCapsSelector from 'editor/src/store/design/selector/getSharedTextFieldCapsSelector';
import getSharedTextFieldTargets from 'editor/src/store/design/selector/getSharedTextFieldTargets';
import getStructureIndexByElementUuidMemoized from 'editor/src/store/design/selector/getStructureIndexByElementUuid';
import { Condition, MediaText, TextValueFormat, TextConditionVariants } from 'editor/src/store/design/types';
import applyValueFormat from 'editor/src/store/design/util/applyValueFormat';
import buildFieldHint from 'editor/src/store/design/util/buildFieldHint';
import resolveTextVariant from 'editor/src/store/design/util/resolveTextVariant';
import { getOperativeCharCap, resolveSharedTextField } from 'editor/src/store/design/util/sharedTextFields';
import updateTextPropertiesWithoutRender from 'editor/src/store/design/util/updateTextPropertiesWithoutRender';
import { isValidValueRegex, matchesValueFormat, MAX_VALUE_REGEX_LENGTH } from 'editor/src/store/design/util/valueRegex';
import selectElementOperation from 'editor/src/store/editor/operation/selectElementOperation';
import getShowValueFormatErrors from 'editor/src/store/editor/selector/getShowValueFormatErrors';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';

import iconError from 'editor/src/static/error-icon.svg';
import iconInfo from 'editor/src/static/info-icon.svg';
import useTr from 'editor/src/util/useTr';

import Accordion from 'editor/src/component/Accordion';
import useDragHandle from 'editor/src/component/ConditionGroupBuilder/ConditionElements/ReorganizableList/useDragHandle';
import useReorganizableListUpdate from 'editor/src/component/ConditionGroupBuilder/ConditionElements/ReorganizableList/useReorganizableListUpdate';
import useOverlay from 'editor/src/component/ConditionGroupBuilder/Overlay/useOverlay';
import IconCheckCircle from 'editor/src/component/Icon/IconCheckCircle';
import ElementName from 'editor/src/component/PersonalizationContent/PersonalizationElements/ElementName';
import useFocusElementOnFlag from 'editor/src/component/PersonalizationContent/PersonalizationElements/useFocusElementOnFlag';
import SelectButtonGroup from 'editor/src/component/SelectButtonGroup';
import { SelectButtonGroupElement } from 'editor/src/component/SelectButtonGroup/SelectButton';
import SharedTextFieldLabel from 'editor/src/component/SharedTextFieldLabel';
import SwitchControl from 'editor/src/component/SwitchControl';
import TextInput from 'editor/src/component/TextInput';
import WithTooltip from 'editor/src/component/WithTooltip';

import styles from './index.module.scss';

interface Props {
  elementId: number;
  isBuilding?: boolean;
  className?: string;
}

const DEFAULT_CHARACTER_LIMIT = 20;

/** Seeds the mask field. Authors rewrite it for their own numbering. */
const DEFAULT_PHONE_MASK = '+1 xxx xxx xxxx';

/**
 * The worked example in the help text, derived from the seed rather than
 * written into the copy, so changing the seed cannot leave the note
 * demonstrating a pattern the field no longer offers — and does not send the
 * sentence back for re-translation.
 */
const MASK_EXAMPLE_INPUT = '2224445555';
export const MASK_EXAMPLE = {
  mask: DEFAULT_PHONE_MASK,
  input: MASK_EXAMPLE_INPUT,
  output: applyValueFormat(MASK_EXAMPLE_INPUT, {
    kind: 'mask',
    mask: DEFAULT_PHONE_MASK,
  }),
};

function PersonalizationText({ elementId, isBuilding, className }: Props) {
  const tr = useTr();

  const {
    element,
    address,
    isSelected,
    valueFormatEnabled,
    hintEnabled,
    variantsEnabled,
    sharedFieldsEnabled,
    onePageDesign,
    groupCharCap,
    showValueFormatErrors,
  } = useSelector((state) => {
    const mediaElement = getMediaElementByUuid(state, elementId) as MediaText | undefined;
    return {
      isSelected: state.editor.selectedElementUuids.includes(elementId),
      element: mediaElement,
      address: getStructureIndexByElementUuidMemoized(state, elementId),
      // The cap the group is actually held to. Read here, off the lookup this
      // selector already does, rather than in the resolve below — which the
      // author's builder skips and which would cost a second walk.
      groupCharCap: mediaElement ? getOperativeCharCap(mediaElement, getSharedTextFieldCapsSelector(state)) : undefined,
      valueFormatEnabled: getHostSetting(state, 'enableTextValueFormat'),
      hintEnabled: getHostSetting(state, 'enableTextFieldHint'),
      variantsEnabled: getHostSetting(state, 'enableTextConditionVariants'),
      sharedFieldsEnabled: getHostSetting(state, 'enableSharedTextFields'),
      showValueFormatErrors: getShowValueFormatErrors(state),
      // With one page there is no page to choose: every candidate is on it, and
      // a select holding one option asks a question with a single answer.
      // SharedTextFieldLabel drops the page from its labels on the same test.
      onePageDesign: (state.design.designData?.spreads.length ?? 0) <= 1,
    };
  }, shallowEqual);

  const [characterLimit, setCharacterLimit] = useState<number | undefined>(
    element?.extra.maxCharCount ? element.extra.maxCharCount : element?.extra?.text.length || DEFAULT_CHARACTER_LIMIT,
  );
  const [isAdditionalOptionsCollapsed, setIsAdditionalOptionsCollapsed] = useState(true);
  const [isCharacterCountLimitEnabled, setIsCharacterCountLimitEnabled] = useState(
    element?.extra.maxCharCount !== undefined,
  );
  const [isLockBoxSizeEnabled, setIsLockBoxSizeEnabled] = useState(element?.extra.lockBoxSize === true);
  const [isFlexibleWidthEnabled, setIsFlexibleWidthEnabled] = useState(element?.extra.flexibleWidth === true);
  const [isMandatoryEnabled, setIsMandatoryEnabled] = useState(element?.extra.isMandatory === true);
  // The buyer's Personalize-tab field only speaks up about a regex mismatch
  // once it has been left, the same rule as the PP field — no error while
  // still mid-type, since a half-typed value almost never matches yet.
  const [isValueTouched, setIsValueTouched] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Every condition in the design, not only the current spread's: a label on
  // the back of the card varies with a picklist that may be stored on the
  // front (or vice versa once the picklist is design-scoped).
  //
  // Through the selector rather than off `spreads` directly: this component is
  // instantiated once per personalisable text, and `spreads` changes on every
  // element edit. getDesignConditions + shallowEqual holds the same array as
  // long as no condition changed, so a keystroke in one field no longer
  // re-renders every other field's panel.
  const conditionChoices = useSelector(getDesignConditions, shallowEqual);
  const textVariants = element?.extra.textVariants;
  // The spread that STORES the picklist, which is where an active-option change
  // has to be dispatched — not necessarily the one being viewed.
  const variantsSpreadIndex = useSelector((state) =>
    textVariants ? getConditionById(state, textVariants.conditionId)?.spreadIndex : undefined,
  );

  // This component is BOTH surfaces: the author's builder (isBuilding) and the
  // Personalize tab, which is the buyer's. Only the buyer's gets lead election
  // — the author is configuring layers and must see every member of a group,
  // not one standing for the rest. The cap is not part of that difference; see
  // effectiveMaxCharCount below.
  //
  // Primitives, not the view object: it carries a freshly built `memberUuids`
  // array that no shallowEqual can see through, and this component is mounted
  // once per personalisable text.
  //
  // Reads the element and its address from `state` rather than closing over
  // the two above: this decides whether the field renders at all, and a
  // selector that closes over the previous render's values answers from them
  // once on every store change before converging. Cheap here, since the
  // builder — where the most panels are mounted — never reaches the lookup.
  const { isSharedLead, sharedMaxCharCount } = useSelector((state) => {
    // Not computed for the builder, which discards both halves.
    if (isBuilding) {
      return { isSharedLead: true, sharedMaxCharCount: undefined };
    }
    const view = resolveSharedTextField(
      state.design.designData,
      getMediaElementByUuid(state, elementId) as MediaText | undefined,
      getStructureIndexByElementUuidMemoized(state, elementId)?.spreadIndex,
    );
    return { isSharedLead: view.isLead, sharedMaxCharCount: view.maxCharCount };
  }, shallowEqual);
  // The group's cap on BOTH surfaces, because on both it is the input's hard
  // limit — TextInput slices the value at it. The author's builder used to pass
  // the layer's own, which let a front capped at 20 be typed past the back's 5
  // and propagate the overrun: the buyer then met "Shorten this to 5" on a
  // value they never typed, the same failure the canvas cap and the join
  // truncation each close from their own side.
  //
  // The author's OWN cap is not hidden by this. It is what the character-limit
  // control reads and writes, which is where a layer's own setting belongs; in
  // the builder nothing else reads this number, since the counter and the hint
  // below are the buyer's.
  const effectiveMaxCharCount = isBuilding ? groupCharCap : sharedMaxCharCount;

  const fieldHint = buildFieldHint(element?.extra.hint, effectiveMaxCharCount, t);
  const valueFormat = element?.extra.valueFormat;
  const valueFormatKind = valueFormat?.kind ?? 'none';
  // The author's own live check, against the layer's current text — not the
  // buyer's field, which stays blur-gated. An author testing a pattern wants
  // to see it react to what is already on the canvas as they type it.
  const regexPattern = valueFormat?.kind === 'regex' ? valueFormat.pattern : '';
  const isRegexPatternValid = !regexPattern || isValidValueRegex(regexPattern);
  const regexMatchesCurrentText =
    valueFormat?.kind === 'regex' && isRegexPatternValid && !!regexPattern && !!element?.extra.text
      ? matchesValueFormat(element.extra.text, valueFormat)
      : undefined;

  // Off by default, so a host that says nothing gets neither control. A field
  // whose design data already carries the value keeps its control either way:
  // hiding it would leave an imported template with configuration in effect
  // and no way to see or undo it.
  const showValueFormat = valueFormatEnabled || !!valueFormat;
  const showHint = hintEnabled || !!element?.extra.hint;
  const variantsCondition = textVariants && conditionChoices.find((c) => c.id === textVariants.conditionId);
  // The living condition, not the raw pointer: a design saved before
  // deleteConditionReducer cleared these can name a picklist that is gone, and
  // an orphan has nothing to show and nothing to undo. Keeping `!!textVariants`
  // here would render a select holding only the "None" already selected, no
  // rows and no note, on an ungated host where `conditionOptions` is empty too.
  const showTextVariants = (variantsEnabled && conditionChoices.length > 0) || !!variantsCondition;

  // Design-wide, for the same reason as the conditions above: the point of a
  // shared field is to reach the OTHER side of the card.
  const sharedFieldTargets = useSelector(getSharedTextFieldTargets, shallowEqual);
  const sharedFieldId = element?.extra.sharedFieldId;
  const sharedFieldChoices = useMemo(
    () => sharedFieldTargets.filter((target) => target.uuid !== elementId),
    [sharedFieldTargets, elementId],
  );
  // What this text shares with TODAY — read from the design, so it says the
  // same thing the operations do. Every gate below keys on this rather than on
  // the raw id, so the two can never disagree about whether a text is sharing.
  const sharedWith = useMemo(
    () => (sharedFieldId ? sharedFieldChoices.filter((target) => target.sharedFieldId === sharedFieldId) : []),
    [sharedFieldChoices, sharedFieldId],
  );
  // What it could newly be tied to, which is a narrower list: a member locked
  // after it joined is still a member, but is no longer a candidate.
  const joinableChoices = useMemo(() => sharedFieldChoices.filter((target) => target.canJoin), [sharedFieldChoices]);

  // A text that already follows a picklist is offered only that picklist and
  // "None". Moving it straight to a DIFFERENT one replaces `textVariants`
  // wholesale and drops every string the author wrote for the old picklist,
  // with no warning — VaryingTextsSection disables its switch for exactly this
  // case, and the rule has to hold from this side too. "None" goes through the
  // clearing path, which warns and is undoable.
  //
  // With authoring disabled nothing new can be assigned either: the capture
  // path in updateMediaElementOperation is gated on the same host setting, so a
  // fresh assignment could never be filled in. What is already there stays
  // visible, and "None" stays reachable, which is the whole point of showing
  // the control on an ungated host.
  //
  // A text already sharing its value with another layer is offered nothing:
  // per-option strings are the author's and a shared value is the buyer's, so
  // one would overwrite the other. See updateSharedTextFieldOperation, which
  // refuses the pairing from its side too.
  //
  // Keyed on having a partner in the DESIGN, not on the raw id: a group left
  // holding one member — a design saved before the delete path cleaned up after
  // itself — is not sharing with anything, and closing this block for it would
  // wedge the author, telling them to stop sharing while "Same value as"
  // already reads None. The one signal is what keeps the way out open.
  //
  // Not the list of layers this text could be TIED to, either. That list drops
  // locked and decorative layers, and a group holding one of those would read
  // as not sharing here while updateTextConditionVariantsOperation, which keys
  // on the id, went on refusing — a dead click with no note to explain it.
  const conditionOptions = useMemo<Condition[]>(() => {
    if (variantsCondition) {
      return [variantsCondition];
    }
    if (sharedWith.length > 0) {
      return [];
    }
    return variantsEnabled ? conditionChoices : [];
  }, [variantsCondition, variantsEnabled, conditionChoices, sharedWith]);

  // The picklists the move has to go through "None" to reach: rendered, but
  // closed. Leaving them out of the list entirely teaches the author nothing —
  // a label on "Language" that belongs on "Office" would just find "Office"
  // missing, with no route and no reason. VaryingTextsSection has the mirror of
  // this and names the owning picklist next to its disabled switch; the note
  // below carries the reason on this side.
  //
  // Empty on an ungated host, where nothing can be assigned by any route, so
  // pointing at one would be a lie.
  const blockedConditionOptions = useMemo<Condition[]>(
    () =>
      variantsEnabled && variantsCondition
        ? conditionChoices.filter((choice) => choice.id !== variantsCondition.id)
        : [],
    [variantsEnabled, variantsCondition, conditionChoices],
  );

  // A field already in a group keeps its control on an ungated host: hiding it
  // would leave an imported template sharing a value with no way to see that it
  // does, or to stop it. Same rule as the four controls above.
  const showSharedField = (sharedFieldsEnabled && joinableChoices.length > 0) || sharedWith.length > 0;
  // Ungated, the select still holds — and still reaches "None", the one move
  // that is always allowed — but offers no new layer to tie this one to. Same
  // for a text that already varies by picklist: the two are exclusive, see
  // `conditionOptions` above.
  // The group's own members are never offered, in any state: the operation
  // early-returns when the pick is already a member, so a group of three would
  // let the author pick the third from the first and watch the select snap back
  // with no note. They are named in the list above the picker instead, which is
  // where the question "what is this tied to?" is now answered — the picker
  // only ever answers "what shall I tie it to next?".
  const sharedFieldOptions = useMemo(
    () =>
      sharedFieldsEnabled && !textVariants
        ? joinableChoices.filter(
            (target) => !target.variesByPicklist && !(sharedFieldId && target.sharedFieldId === sharedFieldId),
          )
        : [],
    [sharedFieldsEnabled, textVariants, joinableChoices, sharedFieldId],
  );
  // Shown but closed, rather than absent: the operation refuses this pairing,
  // so leaving them selectable would snap the select back with no explanation.
  // Mirrors `blockedConditionOptions` on the picklist side.
  const blockedSharedFieldOptions = useMemo(
    () => (sharedFieldsEnabled && !textVariants ? joinableChoices.filter((target) => target.variesByPicklist) : []),
    [sharedFieldsEnabled, textVariants, joinableChoices],
  );
  // The pages the picker can reach, in design order, each named once. Built
  // from what is on offer rather than from the spread list, so a page holding
  // nothing this text could ever be tied to is not offered as a dead end.
  //
  // Pages whose only entries are closed are still in: the closed rows are what
  // teach the author why a layer they are looking for cannot be picked, and a
  // page missing from the list teaches nothing at all.
  const sharedFieldPages = useMemo(() => {
    const pages = new Map<number, string>();
    [...sharedFieldOptions, ...blockedSharedFieldOptions].forEach((target) => {
      if (!pages.has(target.spreadIndex)) {
        pages.set(target.spreadIndex, target.spreadName);
      }
    });
    return [...pages].map(([spreadIndex, spreadName]) => ({ spreadIndex, spreadName }));
  }, [sharedFieldOptions, blockedSharedFieldOptions]);

  // Closed at rest. Most texts share nothing, and a picker plus its paragraph
  // of prose on every one of them buries the settings below it.
  const [isSharedPickerOpen, setIsSharedPickerOpen] = useState(false);
  const [pickedSpreadIndex, setPickedSpreadIndex] = useState<number | undefined>(undefined);
  // Derived rather than trusted: the pick outlives the list it was made from.
  // Joining the last free layer on a page takes that page out of the list, and
  // a select still pointing at it would show a page with no layers under it.
  //
  // One page on offer needs no choosing — the common two-sided card, seen from
  // one side — so it stands selected and the author picks a layer straight away.
  const pickerSpreadIndex = useMemo(() => {
    if (sharedFieldPages.some((page) => page.spreadIndex === pickedSpreadIndex)) {
      return pickedSpreadIndex;
    }
    return sharedFieldPages.length === 1 ? sharedFieldPages[0].spreadIndex : undefined;
  }, [sharedFieldPages, pickedSpreadIndex]);

  // What the second select holds: the layers of the chosen page only. Named by
  // the layer alone, since the page is no longer part of the choice.
  const pickerFieldOptions = useMemo(
    () => sharedFieldOptions.filter((target) => target.spreadIndex === pickerSpreadIndex),
    [sharedFieldOptions, pickerSpreadIndex],
  );
  const pickerBlockedFieldOptions = useMemo(
    () => blockedSharedFieldOptions.filter((target) => target.spreadIndex === pickerSpreadIndex),
    [blockedSharedFieldOptions, pickerSpreadIndex],
  );

  // Offered only to a text that shares nothing yet. Adding a THIRD layer from
  // inside a group reads backwards — the author is looking at a statement about
  // two layers and is asked to extend it with a picker that answers "which
  // other one", which is the newcomer's question and not this one's. It is not
  // a move that goes missing: the third layer's own panel offers exactly this
  // control, pointed the way the author is already thinking.
  const canOfferSharedPicker = sharedWith.length === 0 && sharedFieldPages.length > 0;

  // Everything in this block except the link: the group, the picker, and the
  // two notes explaining why neither is on offer. One flag rather than two,
  // because the heading and the paragraph turn out to want the same thing —
  // when the only thing under the heading is the link, the link already says it
  // in more words ("Same value as" over "Share this value with another layer"
  // is one label written twice), and a paragraph about what sharing means
  // describes something that is not there.
  const showSharedFieldBody = sharedWith.length > 0 || isSharedPickerOpen || !!textVariants || !sharedFieldsEnabled;

  const dragHandle = useDragHandle();

  useEffect(() => {
    updateList();
  }, [!!element?.name]);

  const updateList = useReorganizableListUpdate();

  const onSwitcherTransitionEnd = useCallback(() => {
    updateList();
  }, [updateList]);

  const onSelectElement = useCallback(() => {
    if (isBuilding) {
      dispatch(selectElementOperation(elementId));
    }
  }, [isBuilding, dispatch, elementId]);

  const onAdditionalOptionsCollapseToggle = useCallback(
    (isCollapsed: boolean) => {
      setIsAdditionalOptionsCollapsed(isCollapsed);
      updateList();
    },
    [setIsAdditionalOptionsCollapsed, updateList],
  );

  const onTextChange = useCallback(
    (text: string) => {
      if (address && element) {
        const elementUpdate = { extra: { ...element.extra, text } };
        updateTextPropertiesWithoutRender(element, elementUpdate, undefined);
        dispatch(updateMediaElementOperation(address, elementUpdate, MediaUpdateActionName.TEXT_UPDATED, true));
      }
    },
    [address, element?.extra],
  );

  const onToggleCharacterLimitSwitch = useCallback(
    (isSwitchedOn: boolean) => {
      setIsCharacterCountLimitEnabled(isSwitchedOn);
      if (isSwitchedOn) {
        const maxCharValue = element?.extra?.maxCharCount
          ? element.extra.maxCharCount
          : element?.extra?.text.length || DEFAULT_CHARACTER_LIMIT;

        setCharacterLimit(maxCharValue);
        onCharacterLimitInputChange(maxCharValue);
      }

      if (!isSwitchedOn && characterLimit) {
        onCharacterLimitInputChange(undefined);
      }
    },
    [characterLimit, element?.extra],
  );

  const onCharacterLimitInputChange = useCallback(
    (value: number | undefined) => {
      if (value === 0) {
        return;
      }

      if (address && element) {
        let elementUpdate;
        if (value !== undefined && value < element.extra.text.length) {
          // On a shared field this truncation reaches the whole group, and that
          // is deliberate — unlike the Required toggle, which is gated out of
          // propagation precisely because it does NOT change the value. Here
          // the value genuinely changes, and a group is one string: leaving the
          // other members on the old, longer one is the divergence the feature
          // exists to prevent. Shortening it shortens it everywhere.
          elementUpdate = {
            extra: {
              ...element.extra,
              maxCharCount: value,
              text: element.extra.text.slice(0, value),
            },
          };
        } else {
          elementUpdate = { extra: { ...element.extra, maxCharCount: value } };
        }

        dispatch(updateMediaElementOperation(address, elementUpdate, MediaUpdateActionName.TEXT_UPDATED, true));
        setCharacterLimit(value);
      }
    },
    [address, element?.extra],
  );

  const onVariantsConditionChange = useCallback(
    (conditionId: string) => {
      if (!element) {
        return;
      }
      // Only what the select actually offers (see `conditionOptions`) — an id
      // outside that list is a reassignment or an ungated assignment, and both
      // would lose the author's strings. Guarding here rather than trusting the
      // rendered options, because this is where the data is destroyed.
      if (conditionId !== 'none' && !conditionOptions.some((c) => c.id === conditionId)) {
        return;
      }
      const condition = conditionOptions.find((c) => c.id === conditionId);
      // Defaults to label mode, same as VaryingTextsSection's switch: the
      // common case needs no authoring, and "own text per option" is a click
      // away below. `values` starts empty — label mode ignores it.
      const next: TextConditionVariants | undefined = condition
        ? { conditionId, useOptionLabel: true, values: {} }
        : undefined;
      dispatch(updateTextConditionVariantsOperation(element.uuid, next));
    },
    [element?.uuid, conditionOptions],
  );

  // The two ways a switched-on text can get its content, mirroring
  // VaryingTextsSection's per-row control. See `TextConditionVariants.useOptionLabel`.
  const onVariantsModeChange = useCallback(
    (mode: 'label' | 'own') => {
      if (!element || !textVariants) {
        return;
      }
      if (mode === 'label') {
        dispatch(
          updateTextConditionVariantsOperation(element.uuid, {
            conditionId: textVariants.conditionId,
            useOptionLabel: true,
            values: textVariants.values,
          }),
        );
        return;
      }
      const values = { ...textVariants.values };
      (variantsCondition?.options ?? []).forEach((option) => {
        if (!values[option.id]) {
          values[option.id] = { text: option.label };
        }
      });
      dispatch(updateTextConditionVariantsOperation(element.uuid, { conditionId: textVariants.conditionId, values }));
    },
    [element, textVariants, variantsCondition],
  );

  const variantsModeItems: SelectButtonGroupElement<'label' | 'own'>[] = useMemo(
    () => [
      { value: 'label', label: tr('editor-text-variants-mode-label', 'Option name') },
      { value: 'own', label: tr('editor-text-variants-mode-own', 'Custom text') },
    ],
    [tr],
  );

  // Editing happens on the canvas, per option: activating an option here is
  // the "now I am doing Japanese" step, and every edit while it is active
  // lands in that option's content (see updateMediaElementOperation).
  //
  // Not gated on the authoring setting. Activating an option also APPLIES the
  // stored variant to the canvas, which is how a read-only host sees what each
  // option holds — and the same dispatch is reachable from the picklist itself
  // (VaryingTextsSection's OptionsDropDown, and the buyer's own dropdown), so
  // withholding it here would only make this one surface arbitrarily stricter
  // than the two next to it.
  //
  // One accepted consequence: the text field above stays live on an ungated
  // host, where the capture path in updateMediaElementOperation is gated off,
  // so typing there edits the base text and lands in no option — and the next
  // activation replaces it with the stored variant. That is the point of the
  // gate (the ungated host is the personalizer, where the typing is the
  // BUYER'S and must not be captured into the author's label), so the field
  // cannot be disabled; the read-only note says so in words instead. Gating
  // activation would not close it either, since the buyer's own picklist
  // reaches the same operation.
  const onActivateVariantOption = useCallback(
    (optionId: string) => {
      if (!variantsCondition || variantsSpreadIndex === undefined) {
        return;
      }
      dispatch(
        updateConditionActiveOptionOperation(
          { spreadIndex: variantsSpreadIndex, conditionId: variantsCondition.id },
          optionId,
        ),
      );
    },
    [variantsCondition, variantsSpreadIndex],
  );

  const onHintChange = useCallback(
    (value: string) => {
      if (!address || !element) {
        return;
      }
      const hint = value.trim() ? value : undefined;
      dispatch(
        updateMediaElementOperation(
          address,
          { extra: { ...element.extra, hint } },
          MediaUpdateActionName.TEXT_UPDATED,
          true,
        ),
      );
    },
    [address, element?.extra, dispatch],
  );

  const onValueFormatChange = useCallback(
    (kind: string) => {
      if (!address || !element) {
        return;
      }
      // The presets only seed the field. A mask is a free text pattern, so an
      // author whose numbers do not look like the default can rewrite it
      // instead of waiting for another entry in this list.
      const presets: Record<string, TextValueFormat | undefined> = {
        none: undefined,
        mask: { kind: 'mask', mask: DEFAULT_PHONE_MASK },
        emailDomain: { kind: 'emailDomain', domain: '' },
        // Starts empty rather than seeded: unlike the mask, there is no
        // pattern that is a safe default for every author's field, and an
        // empty pattern is simply not enforced (see matchesValueFormat).
        regex: { kind: 'regex', pattern: '' },
      };
      dispatch(
        updateMediaElementOperation(
          address,
          { extra: { ...element.extra, valueFormat: presets[kind] } },
          MediaUpdateActionName.TEXT_UPDATED,
          true,
        ),
      );
    },
    [address, element?.extra, dispatch],
  );

  const onMaskChange = useCallback(
    (mask: string) => {
      if (!address || !element || element.extra.valueFormat?.kind !== 'mask') {
        return;
      }
      dispatch(
        updateMediaElementOperation(
          address,
          { extra: { ...element.extra, valueFormat: { kind: 'mask', mask } } },
          MediaUpdateActionName.VALUE_FORMAT_UPDATED,
          true,
        ),
      );
    },
    [address, element?.extra, dispatch],
  );

  const onMaskBlur = useCallback(() => {
    if (!address || !element) {
      return;
    }
    // The mask reaches the value once the author has finished writing it.
    // Re-pouring on every keystroke walks the value through each half-written
    // pattern and destroys it; never re-pouring leaves the value carrying the
    // separators of the mask it replaced. Blur is where the pattern is done.
    dispatch(
      updateMediaElementOperation(address, { extra: { ...element.extra } }, MediaUpdateActionName.TEXT_UPDATED, true),
    );
  }, [address, element?.extra, dispatch]);

  const onEmailDomainChange = useCallback(
    (domain: string) => {
      if (!address || !element || element.extra.valueFormat?.kind !== 'emailDomain') {
        return;
      }
      dispatch(
        updateMediaElementOperation(
          address,
          {
            extra: {
              ...element.extra,
              valueFormat: { kind: 'emailDomain', domain },
            },
          },
          MediaUpdateActionName.TEXT_UPDATED,
          true,
        ),
      );
    },
    [address, element?.extra, dispatch],
  );

  const onRegexPatternChange = useCallback(
    (pattern: string) => {
      if (!address || !element || element.extra.valueFormat?.kind !== 'regex') {
        return;
      }
      // Committed on every keystroke, like the mask: a regex has nothing to
      // re-pour on blur, since it never touches the stored value (see
      // applyValueFormat), so there is no reason to wait for blur to save it.
      dispatch(
        updateMediaElementOperation(
          address,
          { extra: { ...element.extra, valueFormat: { kind: 'regex', pattern } } },
          MediaUpdateActionName.TEXT_UPDATED,
          true,
        ),
      );
    },
    [address, element?.extra, dispatch],
  );

  // Choosing a page only narrows what the layer select shows. It never changes
  // the grouping: an author browsing for a layer to tie this one to must be
  // able to look at another page and change their mind without the text they
  // already share with being cut loose behind them. Leaving is its own button.
  const onSharedFieldPageChange = useCallback((value: string) => {
    setPickedSpreadIndex(value === 'none' ? undefined : Number(value));
  }, []);

  const onSharedFieldChange = useCallback(
    (value: string) => {
      if (!element || value === 'none') {
        return;
      }
      // Only what the select actually offers, on the page it is actually
      // showing — an id outside that would rewrite the grouping of an element
      // this panel never put in front of the author.
      const targetUuid = Number(value);
      if (!pickerFieldOptions.some((target) => target.uuid === targetUuid)) {
        return;
      }
      dispatch(updateSharedTextFieldOperation(element.uuid, targetUuid));
      // The move is made and its result is stated in the list above, so the
      // control that made it steps back out of the way.
      setIsSharedPickerOpen(false);
      setPickedSpreadIndex(undefined);
    },
    [element?.uuid, pickerFieldOptions, dispatch],
  );

  // Always available, on every host and in every state: a text that shares its
  // value must be able to stop, or an imported template can never be untangled.
  // Same reasoning the two operations use for allowing "leave" unconditionally.
  const onStopSharingField = useCallback(() => {
    if (!element) {
      return;
    }
    dispatch(updateSharedTextFieldOperation(element.uuid, undefined));
  }, [element?.uuid, dispatch]);

  const onToggleMandatory = useCallback(
    (on: boolean) => {
      if (!address || !element) {
        return;
      }
      setIsMandatoryEnabled(on);
      const elementUpdate = {
        extra: {
          ...element.extra,
          isMandatory: on ? true : undefined,
        },
      };
      dispatch(updateMediaElementOperation(address, elementUpdate, MediaUpdateActionName.TEXT_UPDATED, true));
    },
    [address, element?.extra, dispatch],
  );

  const onToggleLockBoxSize = useCallback(
    (on: boolean) => {
      if (!address || !element) {
        return;
      }
      setIsLockBoxSizeEnabled(on);
      if (on) {
        setIsFlexibleWidthEnabled(false);
      }
      const elementUpdate = {
        extra: {
          ...element.extra,
          lockBoxSize: on ? true : undefined,
          ...(on ? { flexibleWidth: undefined } : {}),
        },
      };
      dispatch(updateMediaElementOperation(address, elementUpdate, MediaUpdateActionName.TEXT_UPDATED, true));
    },
    [address, element?.extra, dispatch],
  );

  const onToggleFlexibleWidth = useCallback(
    (on: boolean) => {
      if (!address || !element) {
        return;
      }
      setIsFlexibleWidthEnabled(on);
      if (on) {
        setIsLockBoxSizeEnabled(false);
      }
      const elementUpdate = {
        extra: {
          ...element.extra,
          flexibleWidth: on ? true : undefined,
          ...(on ? { lockBoxSize: undefined } : {}),
        },
      };
      updateTextPropertiesWithoutRender(element, elementUpdate, undefined);
      dispatch(updateMediaElementOperation(address, elementUpdate, MediaUpdateActionName.TEXT_UPDATED, true));
    },
    [address, element?.extra, dispatch],
  );

  const textEditorOverlay = useOverlay();
  const divRef = useRef<HTMLDivElement>(null);

  const active = textEditorOverlay.visible || isSelected;
  useFocusElementOnFlag(active, divRef);

  // In the builder the card is collapsed to just its header (like "Other
  // layers") and only expands to show the input + options when selected.
  // Re-measure the reorganizable list whenever that expand/collapse changes.
  const showDetails = !isBuilding || active;
  useEffect(() => {
    updateList();
  }, [showDetails]);

  // `!isBuilding` is the buyer's Personalize tab: a shared value is asked for
  // once per spread there, so the members that are not standing for it step aside
  // rather than presenting two inputs that overwrite each other.
  if (!element || !address || element?.personalizationLocked || (!isBuilding && !isSharedLead)) {
    return null;
  }

  const storedText = !isBuilding && element.sample ? '' : element.extra.text || '';
  // The buyer types the local part; the domain is the template's. Only their
  // half is bound to the input — the whole address used to be, and since the
  // store re-appends the domain on every keystroke the caret was thrown past
  // it, turning "elina" into "e@whitecase.comlina".
  //
  // The author's builder keeps the stored string: it configures the layer and
  // has to see the value the design carries, domain included.
  const forcedDomain =
    !isBuilding && element.extra.valueFormat?.kind === 'emailDomain'
      ? element.extra.valueFormat.domain.replace(/^@/, '')
      : '';
  const inputValue =
    !isBuilding && element.extra.valueFormat?.kind === 'emailDomain' ? storedText.split('@')[0] : storedText;
  // The cap counts the stored address, so the forced "@domain" is reserved
  // out of it — otherwise a local part that fits the input still fails the
  // same cap at checkout.
  const inputMaxCharCount =
    effectiveMaxCharCount !== undefined && forcedDomain
      ? Math.max(0, effectiveMaxCharCount - forcedDomain.length - 1)
      : effectiveMaxCharCount;

  // Shown after the field's own blur, or — for a field the buyer never
  // touched — once a save has already been blocked over it (see
  // sendDesignDataPostMessage's gate and showValueFormatErrors). Required and
  // MaxCharCount are unrelated checks with their own display, untouched here.
  const showValueFormatError =
    !isBuilding &&
    (isValueTouched || showValueFormatErrors) &&
    !matchesValueFormat(element.extra.text, element.extra.valueFormat);

  return (
    <div
      ref={divRef}
      className={cn(className, styles.PersonalizationText, 'cy-personalization-text', {
        [styles.building]: isBuilding,
        [styles.active]: active,
        [styles.static]: !isBuilding,
      })}
      onMouseDown={dragHandle.onMouseDown}
      onClick={onSelectElement}
    >
      <ElementName element={element} isBuilding={isBuilding} address={address} />
      {showDetails && (
        <div
          className={cn(styles.textContainer, {
            [styles.isBuilding]: isBuilding,
          })}
        >
          <div className={styles.inputRow}>
            <TextInput
              value={inputValue}
              className={cn(styles.input, { [styles.invalid]: showValueFormatError })}
              maxCharCount={inputMaxCharCount}
              placeholder={!isBuilding && element?.sample ? element?.extra.text : element?.name}
              onChange={onTextChange}
              onBlur={!isBuilding ? () => setIsValueTouched(true) : undefined}
              aria-invalid={showValueFormatError}
            />
            {showValueFormatError && (
              <img src={iconError} alt="" className={cn(styles.valueFormatErrorIcon, 'cy-value-format-error-icon')} />
            )}
            {/* The half of the address the template owns, shown only on the
                buyer's side: their input holds the local part alone, and an
                email field that never shows an email reads as broken. The
                author's input already carries the whole stored string. */}
            {forcedDomain && <span className={cn(styles.forcedDomain, 'cy-forced-domain')}>@{forcedDomain}</span>}
          </div>
          {!isBuilding && fieldHint && <div className={cn(styles.fieldHint, 'cy-field-help')}>{fieldHint}</div>}
          {!isBuilding && effectiveMaxCharCount && (
            <div className={styles.charCounter}>
              {element?.extra.text.length}/{effectiveMaxCharCount}
            </div>
          )}
        </div>
      )}
      {isBuilding && active && (
        <div className={styles.textOptions}>
          <Accordion
            header={t('Text options')}
            headerClassName={cn(styles.textOptionsHeader, 'cy-text-options-accordion')}
            collapsed={isAdditionalOptionsCollapsed}
            setCollapsed={onAdditionalOptionsCollapseToggle}
            onTransitionEnd={onSwitcherTransitionEnd}
          >
            <div className={styles.characterLimitOption}>
              <div className={styles.switchControl}>
                <div className={styles.characterLimitOptionTitle}>{t('Character count limit')}</div>
                <SwitchControl
                  title=""
                  className="cy-character-count-switch"
                  on={isCharacterCountLimitEnabled}
                  disabled={false}
                  onSwitch={onToggleCharacterLimitSwitch}
                />
              </div>
              <div
                onTransitionEnd={onSwitcherTransitionEnd}
                className={cn(styles.characterLimitInputContainer, {
                  [styles.visible]: isCharacterCountLimitEnabled,
                })}
              >
                {isCharacterCountLimitEnabled && (
                  <input
                    className={cn(styles.characterLimitInput, 'cy-character-limit-input')}
                    value={characterLimit}
                    type="number"
                    min={element?.extra?.text?.length ?? 1}
                    onChange={(e) => onCharacterLimitInputChange(Number(e.target.value))}
                  />
                )}
              </div>
              {showValueFormat && (
                <div className={styles.option}>
                  <div className={styles.optionTitle}>{tr('editor-value-format', 'Forced format')}</div>
                  <select
                    className={cn(styles.optionInput, 'cy-value-format-select')}
                    value={valueFormatKind}
                    onChange={(e) => onValueFormatChange(e.target.value)}
                  >
                    <option value="none">{tr('editor-value-format-none', 'None')}</option>
                    <option value="mask">{tr('editor-value-format-mask', 'Phone number')}</option>
                    <option value="emailDomain">{tr('editor-value-format-email-domain', 'Email domain')}</option>
                    <option value="regex">{tr('editor-value-format-regex', 'Custom pattern (regex)')}</option>
                  </select>
                  {element?.extra.valueFormat?.kind === 'mask' && (
                    <>
                      <TextInput
                        value={element.extra.valueFormat.mask}
                        onChange={onMaskChange}
                        onBlur={onMaskBlur}
                        // Commit each keystroke so the mask in the store is current
                        // when blur re-pours the value; it is cheap, since a mask
                        // edit no longer touches the value.
                        debounceTime={0}
                        className={cn(styles.optionInput, 'cy-value-format-mask-input')}
                        placeholder={DEFAULT_PHONE_MASK}
                      />
                      <div className={styles.optionNote}>
                        {tr(
                          'editor-value-format-mask-note',
                          'Each x takes one digit the buyer types. Everything else is kept exactly as written, so “{{mask}}” shows {{input}} as “{{output}}”.',
                          MASK_EXAMPLE,
                        )}
                      </div>
                    </>
                  )}
                  {element?.extra.valueFormat?.kind === 'emailDomain' && (
                    <TextInput
                      value={element.extra.valueFormat.domain}
                      onChange={onEmailDomainChange}
                      className={cn(styles.optionInput, 'cy-email-domain-input')}
                      placeholder={tr('editor-value-format-email-domain-placeholder', 'example.com')}
                    />
                  )}
                  {element?.extra.valueFormat?.kind === 'regex' && (
                    <div className={styles.regexRow}>
                      <TextInput
                        value={element.extra.valueFormat.pattern}
                        onChange={onRegexPatternChange}
                        // Committed per keystroke, like the mask — see onRegexPatternChange.
                        debounceTime={0}
                        maxCharCount={MAX_VALUE_REGEX_LENGTH}
                        className={cn(styles.optionInput, styles.regexInput, 'cy-value-format-regex-input', {
                          // No message beside it: a half-typed pattern is invalid far
                          // more often than not, and a message on every keystroke would
                          // be more noise than help. The red border is enough of a cue
                          // while the author is still writing it.
                          [styles.invalid]: !isRegexPatternValid,
                        })}
                        // A literal example, not a translated string: it is code the
                        // author copies and edits, not language shown to anyone.
                        placeholder="[A-Z]{2}-\d{4}"
                      />
                      {regexMatchesCurrentText === true && (
                        <IconCheckCircle className={cn(styles.regexCheckIcon, 'cy-value-format-regex-match')} />
                      )}
                      {regexMatchesCurrentText === false && (
                        <img
                          src={iconError}
                          alt=""
                          className={cn(styles.regexMismatchIcon, 'cy-value-format-regex-mismatch')}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
              {showHint && (
                <div className={styles.option}>
                  <div className={styles.optionTitle}>{tr('editor-field-hint', 'Hint')}</div>
                  <TextInput
                    value={element?.extra.hint || ''}
                    onChange={onHintChange}
                    className={cn(styles.optionInput, 'cy-field-hint-input')}
                    placeholder={tr('editor-field-hint-placeholder', 'e.g. Must include country code')}
                  />
                </div>
              )}
              {showTextVariants && (
                <div className={styles.option}>
                  <div className={styles.optionTitle}>{tr('editor-text-variants', 'Vary by picklist')}</div>
                  <select
                    className={cn(styles.optionInput, 'cy-text-variants-select')}
                    // The condition, not the stored id: a design saved before
                    // deleteConditionReducer cleared these can name a picklist
                    // that no longer exists, and a value with no matching
                    // option leaves the select showing something arbitrary.
                    value={variantsCondition?.id ?? 'none'}
                    onChange={(e) => onVariantsConditionChange(e.target.value)}
                  >
                    <option value="none">{tr('editor-value-format-none', 'None')}</option>
                    {conditionOptions.map((condition) => (
                      <option key={condition.id} value={condition.id}>
                        {condition.name}
                      </option>
                    ))}
                    {blockedConditionOptions.map((condition) => (
                      <option key={condition.id} value={condition.id} disabled>
                        {condition.name}
                      </option>
                    ))}
                  </select>
                  {!textVariants && sharedWith.length > 0 && (
                    <div className={cn(styles.optionNote, styles.optionNoteReadOnly, 'cy-text-variants-blocked-note')}>
                      {tr(
                        'editor-text-variants-blocked-by-shared',
                        'This text holds one value shared with another layer, which is the buyer’s to type — so it cannot also keep a separate value per option. Stop it sharing first.',
                      )}
                    </div>
                  )}
                  {textVariants && variantsCondition && (
                    <>
                      <SelectButtonGroup
                        className={cn(styles.optionInput, 'cy-text-variants-mode')}
                        disabled={!variantsEnabled}
                        value={textVariants.useOptionLabel ? 'label' : 'own'}
                        onChange={onVariantsModeChange}
                        items={variantsModeItems}
                      />
                      {/* Own-text mode only: in Option name mode every row would
                          read "label → same label", which says nothing. */}
                      {!textVariants.useOptionLabel &&
                        variantsCondition.options.map((option) => {
                          const variant = resolveTextVariant(textVariants, variantsCondition, option.id);
                          const isActive = option.id === variantsCondition.activeOptionId;
                          // A control in both modes: the row activates the option,
                          // which applies its stored text to the canvas. That is
                          // reading, not authoring, so a read-only host keeps it —
                          // it is how the author sees what each option holds.
                          return (
                            <div
                              key={option.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => onActivateVariantOption(option.id)}
                              onKeyDown={(e) => e.key === 'Enter' && onActivateVariantOption(option.id)}
                              className={cn(styles.variantRow, 'cy-text-variant-row', {
                                [styles.variantRowActive]: isActive,
                              })}
                            >
                              <div className={styles.variantLabel}>{option.label}</div>
                              <div className={cn(styles.variantValue, { [styles.variantMissing]: !variant })}>
                                {variant ? variant.text : tr('editor-text-variants-not-set', 'not set yet')}
                              </div>
                            </div>
                          );
                        })}
                      {/* Own-text mode only: label mode has nothing to edit on
                          the canvas, the text is just the picked option's name. */}
                      {!textVariants.useOptionLabel && (
                        <div className={cn(styles.optionNote, { [styles.optionNoteReadOnly]: !variantsEnabled })}>
                          {variantsEnabled
                            ? tr(
                                'editor-text-variants-note',
                                'Pick an option, then edit this text on the canvas — each option keeps its own text and font. An option not set yet keeps the previous one.',
                              )
                            : tr(
                                'editor-text-variants-readonly',
                                'This text already keeps a separate value per option. Editing it is not enabled here — pick “None” to stop it varying.',
                              )}
                        </div>
                      )}
                      {blockedConditionOptions.length > 0 && (
                        <div className={cn(styles.optionNote, 'cy-text-variants-move-note')}>
                          {tr(
                            'editor-text-variants-move-note',
                            'To follow a different picklist, pick “None” first — that clears the text kept for each option, and can be undone.',
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              {showSharedField && (
                <div className={styles.option}>
                  {showSharedFieldBody && (
                    <div className={cn(styles.optionTitle, 'cy-shared-field-title')}>
                      {tr('editor-shared-field', 'Same value as')}
                    </div>
                  )}
                  {/* What this text is tied to today, stated rather than
                      inferred from a select's value. A group of three has no
                      single layer for a select to point at, and the page it is
                      on belongs next to its name — the picker below cannot
                      carry either, since the page there is the author's
                      browsing choice and not a fact about the group. */}
                  {sharedWith.length > 0 && (
                    <div className={cn(styles.sharedMembers, 'cy-shared-field-members')}>
                      {/* One row: what it is tied to, and the way out beside it.
                          Stacked, the link read as a third member of the list. */}
                      <div className={styles.sharedMemberList}>
                        {sharedWith.map((target) => (
                          <div key={target.uuid} className={cn(styles.sharedMember, 'cy-shared-field-member')}>
                            <SharedTextFieldLabel name={target.name} spreadName={target.spreadName} />
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className={cn(styles.sharedUnlink, 'cy-shared-field-unlink')}
                        onClick={onStopSharingField}
                      >
                        {tr('editor-shared-field-unlink', 'Stop sharing')}
                      </button>
                    </div>
                  )}
                  {/* Closed until asked for. Most texts on most designs share
                      nothing, and two selects and a paragraph of prose on every
                      one of them buries the settings underneath. What is open
                      by default is the state — the members above — and tying
                      this text to one is the step the author takes
                      deliberately. */}
                  {canOfferSharedPicker && !isSharedPickerOpen && (
                    <button
                      type="button"
                      className={cn(styles.sharedAddLink, 'cy-shared-field-open-picker')}
                      onClick={() => setIsSharedPickerOpen(true)}
                    >
                      {tr('editor-shared-field-link', 'Share this value with another layer')}
                    </button>
                  )}
                  {canOfferSharedPicker && isSharedPickerOpen && (
                    <>
                      {/* A one-page design has no page to choose: the select
                          would hold a single option naming the page the author
                          is already looking at. It stays for anything larger,
                          even where only one page holds a candidate, because
                          the layer select below names layers alone — take the
                          page away there and nothing says which side they are
                          on. */}
                      {!onePageDesign && (
                        <select
                          className={cn(styles.optionInput, 'cy-shared-field-spread-select')}
                          value={pickerSpreadIndex ?? 'none'}
                          onChange={(e) => onSharedFieldPageChange(e.target.value)}
                        >
                          <option value="none">{tr('editor-shared-field-page-placeholder', 'Choose a page')}</option>
                          {sharedFieldPages.map((page) => (
                            <option key={page.spreadIndex} value={page.spreadIndex}>
                              {page.spreadName}
                            </option>
                          ))}
                        </select>
                      )}
                      <select
                        className={cn(
                          styles.optionInput,
                          !onePageDesign && styles.optionInputStacked,
                          'cy-shared-field-select',
                        )}
                        // Held on the placeholder: this select performs a move,
                        // it does not display a state. What the move produced is
                        // shown in the list above, where the page each member
                        // sits on is named too.
                        value="none"
                        disabled={pickerSpreadIndex === undefined}
                        onChange={(e) => onSharedFieldChange(e.target.value)}
                      >
                        <option value="none">{tr('editor-shared-field-layer-placeholder', 'Choose a layer')}</option>
                        {pickerFieldOptions.map((target) => (
                          <option key={target.uuid} value={target.uuid}>
                            {target.name}
                          </option>
                        ))}
                        {pickerBlockedFieldOptions.map((target) => (
                          <option key={target.uuid} value={target.uuid} disabled>
                            {target.name}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                  {/* Explains the greyed-out entries, so it belongs with the
                      select holding them and not with the closed link. */}
                  {isSharedPickerOpen && pickerBlockedFieldOptions.length > 0 && (
                    <div className={cn(styles.optionNote, 'cy-shared-field-blocked-note')}>
                      {tr(
                        'editor-shared-field-blocked-targets',
                        'The layers shown greyed out keep their own value for each picklist option, so they cannot also hold one shared value. Stop one varying to share with it.',
                      )}
                    </div>
                  )}
                  {/* What sharing means is worth a paragraph while the author is
                      deciding, or while they are looking at a group they have —
                      and the two refusals are not optional in the same way:
                      they explain why the control being looked for is missing.
                      Same condition as the heading, and for the same reason. */}
                  {showSharedFieldBody && (
                    <div
                      className={cn(styles.optionNote, 'cy-shared-field-note', {
                        [styles.optionNoteReadOnly]: !sharedFieldsEnabled || !!textVariants,
                      })}
                    >
                      {!sharedFieldsEnabled &&
                        tr(
                          'editor-shared-field-readonly',
                          'This text already shares its value with another layer. Tying it to a different one is not enabled here — “Stop sharing” still is.',
                        )}
                      {sharedFieldsEnabled &&
                        textVariants &&
                        tr(
                          'editor-shared-field-blocked-by-variants',
                          'This text keeps its own value for each picklist option, which is the author’s to write — so it cannot also hold one value the buyer types. Stop it varying first.',
                        )}
                      {sharedFieldsEnabled &&
                        !textVariants &&
                        tr(
                          'editor-shared-field-note',
                          'Layers sharing a value hold one value between them: the buyer types it once and it prints on every page it appears on. Position, font, required and character limit stay separate.',
                        )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className={styles.mandatoryOption}>
              <div className={styles.switchControl}>
                <div className={styles.mandatoryOptionTitle}>{t('editor-required-field')}</div>
                <SwitchControl
                  title=""
                  className="cy-mandatory-switch"
                  on={isMandatoryEnabled}
                  disabled={false}
                  onSwitch={onToggleMandatory}
                />
              </div>
            </div>
            <div className={styles.lockBoxSizeOption}>
              <div className={styles.switchControl}>
                <div className={styles.lockBoxSizeOptionTitle}>
                  {t('editor-lock-box-size')}
                  <WithTooltip overlay={t('editor-lock-box-size-tooltip')} placement="top">
                    <img
                      className={styles.lockBoxSizeInfoIcon}
                      src={iconInfo}
                      alt={t('editor-lock-box-size')}
                      draggable={false}
                    />
                  </WithTooltip>
                </div>
                <SwitchControl
                  title=""
                  className="cy-lock-box-size-switch"
                  on={isLockBoxSizeEnabled}
                  disabled={false}
                  onSwitch={onToggleLockBoxSize}
                />
              </div>
            </div>
            <div className={styles.lockBoxSizeOption}>
              <div className={styles.switchControl}>
                <div className={styles.lockBoxSizeOptionTitle}>
                  {t('editor-flexible-width')}
                  <WithTooltip overlay={t('editor-flexible-width-tooltip')} placement="top">
                    <img
                      className={styles.lockBoxSizeInfoIcon}
                      src={iconInfo}
                      alt={t('editor-flexible-width')}
                      draggable={false}
                    />
                  </WithTooltip>
                </div>
                <SwitchControl
                  title=""
                  className="cy-flexible-width-switch"
                  on={isFlexibleWidthEnabled}
                  disabled={false}
                  onSwitch={onToggleFlexibleWidth}
                />
              </div>
            </div>
          </Accordion>
        </div>
      )}
    </div>
  );
}

export default React.memo(PersonalizationText);
